// src/utils/firebase/checklistUnlock.js
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase.utils';

/**
 * Checks if a checklist is unlocked for a user
 * @param {string} userID - User ID
 * @param {string} sport - Sport name
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer name
 * @param {string} series - Series name
 * @returns {Promise<Object>} Unlock status object
 */
export const checkChecklistUnlockStatus = async (userID, sport, year, manufacturer, series) => {
  try {
    // Store unlock data in single object structure
    const unlockDocRef = doc(db, `users/${userID}/unlockedChecklists`, 'allUnlocked');
    const unlockSnap = await getDoc(unlockDocRef);
    
    if (unlockSnap.exists()) {
      const unlockData = unlockSnap.data();
      const key = `${sport}_${year}_${manufacturer}_${series}`;
      const seriesUnlockData = unlockData[key] || {};
      return {
        isUnlocked: seriesUnlockData.isUnlocked || false,
        unlockedAt: seriesUnlockData.unlockedAt,
        tokensSpent: seriesUnlockData.tokensSpent || 0
      };
    } else {
      return {
        isUnlocked: false,
        unlockedAt: null,
        tokensSpent: 0
      };
    }
  } catch (error) {
    console.error("Error checking unlock status:", error);
    return {
      isUnlocked: false,
      unlockedAt: null,
      tokensSpent: 0
    };
  }
};

/**
 * Unlocks a checklist for a user
 * @param {string} userID - User ID
 * @param {string} sport - Sport name
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer name
 * @param {string} series - Series name
 * @param {number} tokensSpent - Number of tokens spent
 * @returns {Promise<boolean>} Success status
 */
export const unlockChecklist = async (userID, sport, year, manufacturer, series, tokensSpent) => {
  try {
    // Store unlock data in single object structure
    const unlockDocRef = doc(db, `users/${userID}/unlockedChecklists`, 'allUnlocked');
    
    // Get existing data and update with new series unlock
    const unlockSnap = await getDoc(unlockDocRef);
    const existingData = unlockSnap.exists() ? unlockSnap.data() : {};
    
    const key = `${sport}_${year}_${manufacturer}_${series}`;
    await setDoc(unlockDocRef, {
      ...existingData,
      [key]: {
        sport,
        year,
        manufacturer,
        series,
        isUnlocked: true,
        unlockedAt: Timestamp.now(),
        tokensSpent: tokensSpent
      }
    });
    
    // Update user's token balance in Firestore
    const userDocRef = doc(db, `users/${userID}`);
    const userSnap = await getDoc(userDocRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const currentTokens = userData.tokens || 0;
      const newTokenBalance = currentTokens - tokensSpent;
      
      if (newTokenBalance >= 0) {
        await updateDoc(userDocRef, {
          tokens: newTokenBalance
        });
        return true;
      } else {
        console.error("Insufficient tokens");
        return false;
      }
    } else {
      console.error("User document not found");
      return false;
    }
  } catch (error) {
    console.error("Error unlocking checklist:", error);
    return false;
  }
};
